import { useTheme } from "@/context/ThemeContext";

/**
 * Theme tokens for the immersive För dig feed.
 * Header chrome (fade/tabs/home) is always dark for readability over photos.
 * Card islands still follow light/dark.
 */
export function useForYouTheme() {
  const { theme } = useTheme();
  const dark = theme === "dark";

  return {
    dark,
    /** Dark header scrim for onDark tabs — stronger in light mode over pale shell/photos */
    shellFade: dark
      ? "linear-gradient(to bottom, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.55) 22%, rgba(0,0,0,0.28) 48%, rgba(0,0,0,0.1) 72%, rgba(0,0,0,0) 100%)"
      : "linear-gradient(to bottom, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.72) 18%, rgba(0,0,0,0.48) 40%, rgba(0,0,0,0.22) 62%, rgba(0,0,0,0.08) 80%, rgba(0,0,0,0) 100%)",
    shell: dark ? "bg-[#050C0F] text-white" : "bg-[#F3F5F7] text-foreground",
    article: dark ? "bg-[#050C0F] text-white" : "bg-[#F3F5F7] text-foreground",
    articleMobile: dark
      ? "bg-[#0B171B] text-white"
      : "bg-[#F3F5F7] text-foreground",
    imageIsland: dark
      ? "bg-[#0B171B] shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
      : "bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)] ring-1 ring-black/5",
    infoIsland: dark
      ? "bg-[#13242A] shadow-[0_12px_40px_rgba(0,0,0,0.35)]"
      : "bg-white shadow-[0_12px_40px_rgba(15,23,42,0.08)] ring-1 ring-black/5",
    /** Solid fill under info content — seam stays transparent so photo fade shows through */
    infoMobile: "",
    infoMobileSolid: dark ? "#0B171B" : "#F3F5F7",
    price: dark ? "text-white" : "text-foreground",
    priceSuffix: dark ? "text-white/90" : "text-foreground/70",
    title: dark ? "text-white" : "text-foreground",
    subtitle: dark ? "text-white/80" : "text-muted-foreground",
    statValue: dark ? "text-white" : "text-foreground",
    statLabel: dark ? "text-white/65" : "text-muted-foreground",
    statBorder: dark ? "border-white/15" : "border-black/10",
    // Light: soft sky fill. Dark: same language as green trust pills, but blue.
    equipmentPill: dark
      ? "border border-sky-400/35 bg-sky-400/12 text-sky-100"
      : "border border-[#A8C8E8] bg-[#E8F3FC] text-[#1A6FA8]",
    moreEquip: dark
      ? "text-sky-200/80 underline decoration-sky-300/40"
      : "text-sky-700/80 underline decoration-sky-400/50",
    ctaDesktop: dark
      ? "bg-white text-[#0B171B] hover:bg-white/90"
      : "bg-sky-600 text-white hover:bg-sky-600/90",
    ctaMobile: dark ? "text-white/55" : "text-muted-foreground",
    ctaMobileIcon: dark ? "text-white/70" : "text-foreground/70",
    ctaMobileLabel: dark ? "text-white/70" : "text-foreground/80",
    railBtn: dark
      ? "bg-[#0B171B]/88 ring-white/15 text-white shadow-[0_2px_10px_rgba(0,0,0,0.35)]"
      : "bg-white/92 ring-black/10 text-foreground shadow-[0_2px_10px_rgba(15,23,42,0.12)]",
    galleryNav: dark
      ? "bg-[#0B171B]/55 text-white ring-white/15"
      : "bg-white/90 text-foreground ring-black/10",
    feedNav: dark
      ? "bg-[#13242A]/90 text-white ring-white/25"
      : "bg-white/95 text-foreground ring-black/10 shadow-md",
    empty: dark ? "text-white/60" : "text-muted-foreground",
    /** Home/Dashboard chip — always dark chrome; subtle ring, no hard border */
    dashBtn:
      "text-white/95 hover:text-white bg-black/40 ring-1 ring-white/15 hover:bg-black/50 backdrop-blur-md border-0 shadow-none",
    tabsVariant: "onDark" as const,
    /** Tall soft ramp over photo bottom → info solid (must paint above gallery) */
    imageFadeBottom: dark
      ? "linear-gradient(to top, #0B171B 0%, rgba(11,23,27,0.92) 22%, rgba(11,23,27,0.55) 52%, rgba(11,23,27,0.18) 78%, rgba(11,23,27,0) 100%)"
      : "linear-gradient(to top, #F3F5F7 0%, rgba(243,245,247,0.92) 22%, rgba(243,245,247,0.55) 52%, rgba(243,245,247,0.18) 78%, rgba(243,245,247,0) 100%)",
    peekVeil: dark ? "bg-[#050C0F]" : "bg-[#F3F5F7]",
    slideBg: dark ? "bg-[#0B171B]" : "bg-muted",
    sheetSeam: dark ? "before:bg-[#050C0F]" : "before:bg-[#F3F5F7]",
    /** Gallery page dots — darker in light mode for contrast on photos */
    galleryDotActive: dark ? "bg-white" : "bg-black/75",
    galleryDotIdle: dark ? "bg-white/55" : "bg-black/40",
    /** Top image fade under header — always dark like dark-mode */
    imageFadeTop:
      "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.18) 45%, rgba(0,0,0,0) 100%)",
  };
}
